import { logger } from '@/lib/logger';

import { classifyBusinessRule, classifyConflict } from './errorCodes';
import { createAppError, fieldErrorsFromBackend } from './errors';
import { envelopeSchema } from './types';

import type * as z from 'zod/mini';

/**
 * Envelope first, then `data`. A payment page must never render a half-parsed money object.
 *
 * HTTP **402** with `success: true` is a declined API pay-in (CSH-172): the envelope decides,
 * not the status code. Do not throw on 402 when `success` is true.
 */
export function unwrap<T>(body: unknown, schema: z.ZodMiniType<T>, ctx: { status: number }): T {
  const envelope = envelopeSchema(schema).safeParse(body);
  if (!envelope.success) {
    logger.warn('[api] envelope or data failed schema', {
      status: ctx.status,
      issues: envelope.error.issues,
    });
    throw createAppError({
      kind: 'parse',
      code: 'PARSE_ERROR',
      status: ctx.status,
      cause: envelope.error,
    });
  }

  const parsed = envelope.data;
  if (!parsed.success) {
    const business =
      parsed.error.code === 'BUSINESS_RULE'
        ? classifyBusinessRule(parsed.error.message)
        : undefined;
    const conflict =
      parsed.error.code === 'CONFLICT' ? classifyConflict(parsed.error.message) : undefined;
    throw createAppError({
      kind: 'http',
      code: parsed.error.code,
      status: ctx.status,
      requestId: parsed.meta.requestId,
      fieldErrors: conflict?.fieldErrors ?? fieldErrorsFromBackend(parsed.error.fields),
      messageKey: conflict?.messageKey ?? business?.messageKey,
      limitAmount: business?.limitAmount,
      cause: parsed.error,
    });
  }

  return parsed.data;
}
